// ---------------------------------------------------------------------------
// useBackgroundSync — Client-getriggerter bexio-Pull (Phase 5.19).
// ---------------------------------------------------------------------------
// V5-Pattern: bei App-Mount und Navigation zu Domain-Pages (Kunden,
// Rechnungen) wird /api/finance/bexio-sync-now angestossen. Der Endpoint pulled die
// jeweils relevanten Domains aus bexio und schreibt in Supabase. Vercel-Crons
// laufen weiter als Backstop (stuendlich), aber das Live-Gefuehl ("ich sehe
// die letzte bexio-Aenderung sofort, wenn ich /kunden oeffne") entsteht
// hier. /projekte zieht seit BEXIO-11 (23.09.2026) nichts mehr aus bexio:
// Projekte laufen nicht mehr nach bexio und kommen nicht mehr von dort.
//
// Schutzmechanismen:
//   - Throttle 30 s pro Client (sliding window im MODUL, siehe unten).
//     Spiegelt die Server-seitige Skip-Schwelle in
//     /api/finance/bexio-sync-now.
//   - Ein laufender Sync blockiert den naechsten (in-flight-Riegel).
//   - Silent-Fail: Fehler werden in den Logger geschrieben, nie als Toast.
//     Der Sync soll fuer den User unsichtbar bleiben, solange er funktioniert.
//   - React-Query useMutation mit mutationKey `["bexio-sync-now"]` — damit
//     der TopNav-Spinner via `useIsMutating` den laufenden Sync sehen kann.
//
// ---------------------------------------------------------------------------
// FIN-3 (2026-07-29) — «pro Client» stand im Kommentar, im Code stand «pro
// Hook-Instanz».
// ---------------------------------------------------------------------------
// Daniel hat beim Rundgang gemessen: beim Laden der Admin-Startseite feuerte
// POST /api/finance/bexio-sync-now DREIMAL. Der Zaehler lag in einem `useRef`
// — der lebt so lange wie die Komponente und beginnt bei jedem Mount wieder
// bei null. Zwei Mounts kurz hintereinander sind darum zwei Syncs, ganz egal
// was der Kommentar behauptete.
//
// Das ist nicht theoretisch: seit STABIL-12 haengt ein `key` am ganzen Baum
// unter dem AuthProvider (Identitaets-Epoche). Jeder Wechsel der Identitaet —
// Vertretung an, Vertretung aus — wirft AppChrome weg und baut es neu auf.
// Mit einem Ref-Zaehler laeuft dabei jedes Mal ein voller Fuenf-Domain-Pull
// los, obwohl der Pull mit der Identitaet nichts zu tun hat (er holt bexio-
// Daten in UNSERE DB, fuer alle dieselben).
//
// Und der Server faengt das nicht auf: `shouldSkipRecentRun` kann erst
// ueberspringen, NACHDEM `recordRunStart` geschrieben ist. Zwei Aufrufe
// wenige Millisekunden auseinander laufen beide voll durch — und
// `bexio-sync-bills` schreibt pro Lauf 717 Belege.
//
// EHRLICH GESAGT: dass es genau dreimal war, habe ich nicht reproduzieren
// koennen — die Messung war ein Live-Mitschnitt in Produktion und der
// Ausloeser des dritten Aufrufs ist nicht bewiesen. Was hier steht, macht
// mehrere Aufrufe pro 30-s-Fenster strukturell unmoeglich, unabhaengig davon,
// wie viele Mounts es dazu braucht.

import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { apiPost } from "@/lib/api/client";
import { logger } from "@/lib/logger";

// Pfade, die einen Domain-spezifischen Sync ausloesen. Andere Routen feuern
// keinen Sync (Verdienst, Aktivitaeten, Einstellungen — alles ohne bexio-Bezug
// aus User-Sicht).
const PATH_TO_DOMAINS: ReadonlyArray<{
  prefix: string;
  domains: ReadonlyArray<"contacts" | "invoices" | "bills" | "products">;
}> = [
  { prefix: "/kunden", domains: ["contacts"] },
  { prefix: "/finanzen", domains: ["invoices"] },
];

const THROTTLE_MS = 30_000;

export const BACKGROUND_SYNC_MUTATION_KEY = ["bexio-sync-now"] as const;

// Der Zaehler lebt im Modul, nicht in der Komponente — das IST der Fix. Ein
// Browser-Tab laedt dieses Modul genau einmal, also gibt es genau eine Uhr.
const throttle = { lastTriggeredAt: 0, inFlight: false };

/**
 * Setzt den Client-Throttle zurueck.
 *
 * Der Zustand ueberlebt jetzt absichtlich jeden Remount — damit ueberlebt er
 * auch das Ende eines Tests. Tests brauchen deshalb einen Weg, mit einer
 * frischen Uhr zu beginnen; im Produktionscode gibt es keinen Aufrufer.
 */
export function resetBackgroundSyncThrottle(): void {
  throttle.lastTriggeredAt = 0;
  throttle.inFlight = false;
}

interface SyncNowResponse {
  ok: true;
  results: ReadonlyArray<{
    domain: string;
    status: "ok" | "skipped" | "error";
  }>;
}

export function useBackgroundSync(): void {
  const location = useLocation();

  const mutation = useMutation({
    mutationKey: [...BACKGROUND_SYNC_MUTATION_KEY],
    mutationFn: async (
      domains: ReadonlyArray<"contacts" | "invoices" | "bills" | "products">,
    ): Promise<SyncNowResponse> => {
      const body = domains.length === 0 ? {} : { domains };
      return await apiPost<SyncNowResponse>("/api/finance/bexio-sync-now", body);
    },
    onSettled: () => {
      throttle.inFlight = false;
    },
    onError: (err) => {
      // Silent-Fail. Sync soll fuer den User nicht sichtbar fehlschlagen —
      // der Vercel-Cron-Backstop holt fehlende Daten spaeter nach.
      logger.warn(
        { err: err instanceof Error ? err.message : String(err) },
        "useBackgroundSync error (silent)",
      );
    },
  });

  // Mount: Full-Sync (alle Domains).
  useEffect(() => {
    triggerOnce(() => {
      mutation.mutate([]);
    });
    // Mount-only — nur einmal pro Component-Lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pathname-Wechsel: Domain-spezifischer Sync.
  useEffect(() => {
    const match = PATH_TO_DOMAINS.find((entry) =>
      location.pathname.startsWith(entry.prefix),
    );
    if (!match) return;
    triggerOnce(() => {
      mutation.mutate(match.domains);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}

function triggerOnce(fire: () => void): void {
  // Ein laufender Pull blockiert den naechsten. Das Zeitfenster allein reicht
  // nicht: der Server kann einen Doppel-Aufruf erst ueberspringen, wenn der
  // erste seinen Start geschrieben hat — davor arbeiten beide voll.
  if (throttle.inFlight) return;
  const now = Date.now();
  if (now - throttle.lastTriggeredAt < THROTTLE_MS) return;
  throttle.lastTriggeredAt = now;
  throttle.inFlight = true;
  fire();
}
